import React, { useState } from "react";
import { Modal, Tab, Tabs } from "react-bootstrap";
import { ActionHandlerFunction, OrgInfo } from "@preveil-api";
import { Account, AdminErrorMessages, AdminSuccessMessages, AdminUIActionTypes, findDuplicateAddress, findInvalidAddress, Message, RegexHelper, useAddToTrustedCommunityMutation, useAppDispatch } from "src/common";
import { GroupForm, IndividualForm } from "..";
import { uiActions } from "src/store";
import _ from "lodash";

type AllProps = {
  show_modal: boolean;
  current_account: Account
  handleAction: ActionHandlerFunction;
  address_list: string[];
  org_info: OrgInfo;
}

function AddTrustedAddressModalComponent(props: AllProps) {
  const { show_modal, handleAction, address_list, current_account, org_info } = props;
  const dispatch = useAppDispatch();
  const tab_event_key = {
    individual: "Add Trusted Addresses/Domains",
    csvUpload: "Importing Trusted Community Addresses/Domains"
  };
  const [tab_heading, setTabHeading] = useState<string>(tab_event_key.individual);
  const [addToTrustedCommunity] = useAddToTrustedCommunityMutation();

  // Description: Handling changing modal tabs and including the correct header.
  function handleTabSelect(eventKey: string | null) {
    const tab_heading = eventKey === "csvUpload" ? tab_event_key.csvUpload : tab_event_key.individual;
    setTabHeading(tab_heading);
  }

  // Description: handle the validation and submit the new address/domain collection.
  function submitAddresses(collection: string[]) {
    if (_.isEmpty(collection)) {
      return;
    };
    const is_duplicated_found = findDuplicateAddress(address_list, collection);
    const invalid_addresses = findInvalidAddress(collection);
    const is_valid_address = invalid_addresses.length >= 1;

    if (is_duplicated_found) {
      const params = { message: AdminErrorMessages.error_duplicate_trusted_address, stack: {} };
      handleAction({ actionType: AdminUIActionTypes.PageError, params });
    } else if (is_valid_address) {
      const params = { message: AdminErrorMessages.error_invalid_address, stack: {} };
      handleAction({ actionType: AdminUIActionTypes.PageError, params });
    } else {
      handleAction({ actionType: AdminUIActionTypes.ShowModal });
      addToWhitelistAddresses(collection);
    }
  };

  // Description: Handles request to the server to add new addresses/domains.
  async function addToWhitelistAddresses(data: string[]) {
    const { user_id } = current_account;
    const domains = data.filter((element) => RegexHelper.testDomain(element));
    const users = data.filter((element) => RegexHelper.testEmailAddress(element));
    await addToTrustedCommunity({ userId: user_id, orgId: org_info.org_id, users, domains })
      .unwrap()
      .then(() => {
        handleAction({ actionType: AdminUIActionTypes.GetRefreshRequest });
        dispatch(uiActions.handleSetMessage(new Message(AdminSuccessMessages.success_add_trusted_community_address)));
      })
      .catch((error) => {
        const params = {
          message: AdminErrorMessages.error_adding_trusted_community,
          stack: error
        };
        handleAction({ actionType: AdminUIActionTypes.PageError, params });
      });
  }

  return <Modal
    size="lg"
    show={show_modal}
    onHide={() => handleAction({ actionType: AdminUIActionTypes.ShowModal })}
    aria-labelledby="trusted community form"
    centered
  >
    <Modal.Header closeButton>
      <Modal.Title>{tab_heading}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <Tabs defaultActiveKey="individual" id="add-users-tabs" onSelect={handleTabSelect}>
        <Tab eventKey="individual" title="Individual">
          <IndividualForm handleAction={handleAction} submitAddresses={submitAddresses} />
        </Tab>
        <Tab eventKey="csvUpload" title="CSV Upload">
          <GroupForm handleAction={handleAction} address_list={address_list} submitAddresses={submitAddresses} />
        </Tab>
      </Tabs>
    </Modal.Body>
  </Modal>;
}

export default React.memo(AddTrustedAddressModalComponent);
